/* eslint max-lines-per-function: ["error", 80] */
import {html, nothing, LitElement} from 'lit';
import styles from './x-product-card.component.scss?as=stylesheet';
import {ref, createRef} from 'lit/directives/ref.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import GTM from 'extensions/gtm';

const NO_IMAGE_THUMBNAIL = `${window.mivaJS.Image_Placeholder_Product_Empty}`;

export class ProductCard extends LitElement {

	imageRef = createRef();

	static styles = [styles];

	static get properties () {
		return {
			brand: {
				type: String
			},
			code: {
				type: String
			},
			flag: {
				type: String
			},
			gtmListId: {
				attribute: 'gtm-list-id',
				type: String
			},
			gtmListName: {
				attribute: 'gtm-list',
				type: String
			},
			gtmPosition: {
				attribute: 'gtm-position',
				type: Number
			},
			hoverSrcDesktop: {
				attribute: 'hover-src-desktop',
				type: String
			},
			hoverSrcMobile: {
				attribute: 'hover-src-mobile',
				type: String
			},
			imgSrcDesktop: {
				attribute: 'image-src-desktop',
				type: String
			},
			imgSrcMobile: {
				attribute: 'image-src-mobile',
				type: String
			},
			name: {
				type: String
			},
			originalPrice: {
				attribute: 'original-price',
				type: Number
			},

			price: {
				type: Number
			},
			productId: {
				attribute: 'product-id',
				type: String
			},
			quickAdd: {
				attribute: 'quick-add',
				type: Boolean
			},
			sectionTitle: {
				attribute: 'section-title',
				type: String
			},
			shippingFlags: {
				attribute: 'shipping-flags',
				type: Object
			},
			sourceLoaded: {
				state: true,
				type: Boolean
			},
			styleNumber: {
				attribute: 'style-number',
				type: String
			},

			url: {
				type: String
			}
		};
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		this.brand = '';
		this.code = '';
		this.flag = '';
		this.formattedBasePrice = '';
		this.formattedPrice = '';
		this.gtmListId = '';
		this.gtmListName = '';
		this.gtmPosition = 0;
		this.hoverSrcDesktop = '';
		this.hoverSrcMobile = '';
		this.imgHeight = 425;
		this.imgSrcDesktop = '';
		this.imgSrcMobile = '';
		this.imgWidth = 425;
		this.name = '';
		this.noImageSrc = NO_IMAGE_THUMBNAIL;
		this.originalPrice = 0;
		this.price = 0;
		this.productId = '';
		this.styleNumber = '';
		this.shippingFlags = {};
		this.sourceLoaded = false;
		this.url = '';
		this.quickAdd = false;
		this.sectionTitle = '';
		this._modalTeleported = false;
	}

	connectedCallback () {
		super.connectedCallback();
	}

	handleSourceLoad () {
		this.sourceLoaded = true;
	}

	handleClick (e) {
		const product = {
			brand: this.brand,
			code: this.code,
			id: this.styleNumber,
			/* eslint-disable camelcase */
			item_brand: this.brand,
			item_id: this.styleNumber,
			item_list_id: this.gtmListId,
			item_list_name: this.gtmListName,
			item_name: this.name,
			/* eslint-enable camelcase */
			list: this.gtmListName,
			name: this.name,
			position: this.gtmPosition,
			price: this.price
		};

		this.gtm.searchSendProductClick(product);

		// Adjust product data for Searchspring
		product.id = this.productId;
		const ssFields = {
			sku: this.code,
			uid: this.productId,
			url: this.url
		};

		let prodClickEvent = new CustomEvent('product-card-click', {
			bubbles: true,
			cancelable: true,
			composed: true,
			detail: {
				...product,
				...ssFields
			}
		});
		this.dispatchEvent(prodClickEvent);
		if (prodClickEvent.defaultPrevented) {
			e.preventDefault();
		}
	}

	async initImageSwap () {
		// Elements
		const imageSwapElement = this.imageRef;

		if (!imageSwapElement.value.dataset.imageSwap) {
			return;
		}

		// Module
		const {default: ImageSwap} = await import('../../../image-swap');

		new ImageSwap(imageSwapElement.value);
	}

	renderPrice () {
		let strikethroughPriceHtml = '';

		// Price formatter
		const usCurrency = new Intl.NumberFormat('en-US', {
			currency: 'USD',
			style: 'currency'
		});

		if (this.price < this.originalPrice) {
			strikethroughPriceHtml = html`<s class="x-product-card__base-price">${usCurrency.format(this.originalPrice)}</s>`;
		}

		return html`
			<div class="x-product-card__pricing">
				${strikethroughPriceHtml}
				<span class="x-product-card__price">${usCurrency.format(this.price)}</span>
			</div>
		`;
	}

	renderQuickAdd () {
		if (!this.quickAdd) {
			return nothing;
		}

		if (this.shippingFlags?.isInStoreOnly || this.shippingFlags?.isNotForSale) {
			return nothing;
		}

		return html`
				<g-modal
					type="iframe"
					class="g-modal-quick-add-iframe"
					title="true"
					target="${this.url}?QuickAdd=1"
					id="js-quick-add-${this.productId}"
				>
					<header slot="title">${this.sectionTitle || this.name}</header>
				</g-modal>
				<button
					type="button"
					class="g-button g-button-outline x-product-card__button-quick-add"
					aria-label="Quick Add"
					@click="${this._openQuickAdd}"
					data-cnstrc-btn="add_to_cart"
				>+ Add</button>
			`;
	}

	renderShippingFlag () {
		if (!this.shippingFlags) {
			return '';
		}

		if (this.shippingFlags.isBopisOnly) {
			return html`
				<div class="x-product-card__shipping">
					<span class="x-flag-shipping">
						<span class="x-product-card__shipping-icon u-icon-pickup"></span> Pick Up Only
					</span>
				</div>
			`;
		} else if (this.shippingFlags.isInStoreOnly) {
			return html`
				<div class="x-product-card__shipping">
					<span class="x-flag-shipping">
						<span class="x-product-card__shipping-icon u-icon-pickup"></span> In Store Only
					</span>
				</div>
			`;
		} else if (this.shippingFlags.isDeliveryOnly) {
			return html`
				<div class="x-product-card__shipping">
					<span class="x-flag-shipping x-flag-shipping--delivery-only">
						<span class="x-product-card__shipping-icon u-icon-truck"></span> In Store Only
					</span>
				</div>
			`;
		} else if (this.shippingFlags.isNotForSale) {
			return html`
				<div class="x-product-card__shipping">
					<span class="x-flag-shipping">
						<span class="x-product-card__shipping-icon u-icon-cross"></span> Unavailable
					</span>
				</div>
			`;
		}

		return '';
	}

	renderImage () {
		return html`
			<div class="x-product-card__image">
				<picture>
					<source data-srcset="${this.htmlEntities(this.imgSrcMobile || this.noImageSrc)}" media="(max-width: 767px)">
					<img ${ref(this.imageRef)} class="js-image-swap x-product-card__image-source lazyload" src="${this.imgSrcDesktop || this.noImageSrc}" height="${this.imgHeight}" width="${this.imgWidth}" data-src="${this.htmlEntities(this.imgSrcDesktop)}" data-image-swap="${ifDefined(this.hoverSrcDesktop)}" alt="${this.name} image">
				</picture>
			</div>
		`;
	}

	firstUpdated () {
		this.initImageSwap();
	}

	updated () {
		if (this.quickAdd) {
			this._teleportModal();
		}
	}

	/** Teleports the g-modal to document.body to escape the shadow DOM stacking context created by x-splide's CSS transform on .splide__list  **/
	_teleportModal () {
		if (this._modalTeleported) {
			return;
		}

		if (!this.sectionTitle && !this.name) {
			return;
		}

		const modal = this.shadowRoot.querySelector('g-modal');

		if (!modal) {
			return;
		}

		document.body.appendChild(modal);
		this._modalTeleported = true;
	}

	_openQuickAdd () {
		const modal = document.getElementById(`js-quick-add-${this.productId}`);

		if (!modal) {
			return;
		}

		const titleSlot = modal.querySelector('[slot="title"]');
		if (titleSlot) {
			titleSlot.textContent = this.sectionTitle || this.name;
		}

		modal.open();
	}

	render () {
		return html`
			<div class="js-product-card x-product-card x-product-card__hoverable lazyload" @load="${this.handleSourceLoad}" data-cnstrc-item="recommendation" data-cnstrc-item-id="${this.code}" data-cnstrc-item-name="${this.name}" data-cnstrc-item-price="${ifDefined(this.quickAdd ? this.price : void 0)}">
				<a class="x-product-card__link" href="${this.htmlEntities(this.url)}" title="${this.htmlEntities(this.name)}" @click="${this.handleClick}">
				${this.renderImage()}
					<div class="x-product-card__footer">
						<div class="x-product-card__name">${this.htmlEntities(this.name)}</div>
						<div class="x-product-card__information">
							${this.renderPrice()}
						</div>
						${this.renderShippingFlag()}
					</div>
				</a>
				${this.renderQuickAdd()}
			</div>
		`;
	}

	// Helpers
	htmlEntities (str) {
		// eslint-disable-next-line newline-per-chained-call
		return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}
};

window.customElements.define('x-product-card', ProductCard);
